import { useEffect, useMemo, useState } from "react";
import type { LookalikeItem, Species } from "../lib/types";
import { lookalikes } from "../lib/api";
import { enrichForAdd } from "../lib/resolveSpecies";
import { useModalChrome } from "../lib/modalUx";
import { openPhoto } from "./PhotoViewer";
import { PhotoFrame } from "../ui";
import { hasRealCommonName } from "../../shared/species";
import { CloseIcon } from "../ui";

// "Could be confused with" — shown before you commit a sighting. Lists nearby
// look-alikes with a one-line way to tell them apart, and lets you add the
// right one if you realize it's actually the look-alike.
export function LookalikeModal({
  target,
  locationName,
  candidates,
  isSeen,
  onClose,
  onAddSpecies,
}: {
  target: Species | null;
  locationName: string;
  candidates: Species[];
  isSeen: (species: Species) => boolean;
  onClose: () => void;
  onAddSpecies: (s: Species) => void;
}) {
  const [items, setItems] = useState<LookalikeItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byScientific = useMemo(() => {
    const m = new Map<string, Species>();
    for (const s of candidates) {
      m.set(s.scientific_name.trim().toLowerCase(), s);
    }
    return m;
  }, [candidates]);

  // Enrich a thin candidate (no real common name / photo) before adding, so a
  // look-alike pick doesn't strand a bare GBIF entry in the lifelist — the
  // same enrichment the Spot-screen match cards do.
  const addAndClose = async (sp: Species) => {
    onAddSpecies(await enrichForAdd(sp));
    onClose();
  };

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, onClose]);

  useModalChrome(target !== null, onClose);

  useEffect(() => {
    if (!target) {
      setItems(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems(null);
    lookalikes({
      target: {
        common: target.common_name,
        scientific: target.scientific_name,
      },
      location_name: locationName,
      candidates: candidates.map((s) => ({
        common: s.common_name,
        scientific: s.scientific_name,
        count: s.observation_count,
      })),
    })
      .then((r) => {
        if (!cancelled) setItems(r.lookalikes);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Lookup failed.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target, locationName, candidates]);

  if (!target) return null;

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden overscroll-contain rounded-t-3xl bg-paper pb-[env(safe-area-inset-bottom)] shadow-card sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Could be confused with"
      >
        <div className="flex items-center justify-between border-b border-ink/5 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-ink">Could be confused with</h2>
            <p className="truncate text-xs text-ink/50">
              if you think it's {target.common_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink/50 hover:bg-ink/5"
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-3 rounded-xl bg-aqua/10 p-3 ring-1 ring-inset ring-aqua/30">
            {target.photo_url ? (
              <button
                type="button"
                onClick={() =>
                  openPhoto({
                    url: target.photo_url,
                    common_name: target.common_name,
                    scientific_name: target.scientific_name,
                  })
                }
                className="h-12 w-12 shrink-0"
                aria-label={`Enlarge photo of ${target.common_name}`}
              >
                <PhotoFrame
                  src={target.photo_url}
                  alt={target.common_name}
                  hint={target.scientific_name}
                  className="h-full w-full"
                  radiusClass="rounded-lg"
                  size="square"
                  sceneSimple
                />
              </button>
            ) : (
              <PhotoFrame
                src=""
                hint={target.scientific_name}
                className="h-12 w-12 shrink-0"
                radiusClass="rounded-lg"
                size="square"
                sceneSimple
              />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={`truncate text-sm font-semibold text-ink${
                  hasRealCommonName(target.common_name, target.scientific_name)
                    ? ""
                    : " italic"
                }`}
              >
                {hasRealCommonName(target.common_name, target.scientific_name)
                  ? target.common_name
                  : target.scientific_name}
              </p>
              {hasRealCommonName(
                target.common_name,
                target.scientific_name,
              ) && (
                <p className="truncate text-xs italic text-ink/55">
                  {target.scientific_name}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => void addAndClose(target)}
              className={isSeen(target) ? "btn-ghost" : "btn-primary"}
              disabled={isSeen(target)}
            >
              {isSeen(target) ? "Added" : "It's this"}
            </button>
          </div>

          {loading && (
            <p className="py-6 text-center text-sm text-ink/55">
              Checking look-alikes…
            </p>
          )}
          {error && (
            <p className="rounded-xl bg-coral/10 px-4 py-3 text-sm text-coral">
              {error}
            </p>
          )}
          {items && items.length === 0 && !error && (
            <p className="py-4 text-center text-sm text-ink/55">
              No common look-alikes flagged here — likely a clear ID.
            </p>
          )}

          {items?.map((it, i) => {
            const sp = byScientific.get(
              it.scientific_name.trim().toLowerCase(),
            );
            return (
              <div
                key={`${it.scientific_name}-${i}`}
                className="rounded-xl bg-white/80 p-3 ring-1 ring-inset ring-ink/5"
              >
                <div className="flex items-center gap-3">
                  {sp?.photo_url ? (
                    <button
                      type="button"
                      onClick={() =>
                        openPhoto({
                          url: sp.photo_url,
                          common_name: it.common_name,
                          scientific_name: it.scientific_name,
                        })
                      }
                      className="h-12 w-12 shrink-0"
                      aria-label={`Enlarge photo of ${it.common_name}`}
                    >
                      <PhotoFrame
                        src={sp.photo_url}
                        alt={it.common_name}
                        hint={it.scientific_name}
                        className="h-full w-full"
                        radiusClass="rounded-lg"
                        size="square"
                        sceneSimple
                      />
                    </button>
                  ) : (
                    <PhotoFrame
                      src=""
                      hint={it.scientific_name}
                      className="h-12 w-12 shrink-0"
                      radiusClass="rounded-lg"
                      size="square"
                      sceneSimple
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {it.common_name || it.scientific_name}
                    </p>
                    <p className="truncate text-xs italic text-ink/55">
                      {it.scientific_name}
                    </p>
                  </div>
                  {sp ? (
                    <button
                      type="button"
                      onClick={() => void addAndClose(sp)}
                      className={
                        isSeen(sp) ? "btn-ghost" : "btn-accent"
                      }
                      disabled={isSeen(sp)}
                    >
                      {isSeen(sp) ? "Added" : "It's this"}
                    </button>
                  ) : (
                    <span className="shrink-0 text-[11px] text-ink/40">
                      not listed here
                    </span>
                  )}
                </div>
                {it.distinction && (
                  <p className="mt-2 rounded-lg bg-aqua-deep/10 px-3 py-2 text-xs text-ink/75">
                    <span className="font-semibold text-aqua-deep">
                      Tell apart:{" "}
                    </span>
                    {it.distinction}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
